import { dailySeed, hashString32 } from "./seededRng";

// Each daily challenge modifier rebrands an otherwise-normal endless
// run with one big rule change. Picked deterministically from the
// dailyKey so every player on the same UTC day gets the same modifier.
//
// The set is intentionally small + each modifier is "one knob" so the
// player can recognise what changed within a few seconds of playing.
//   mirror_day  -- every shape segment is a mirror trick
//   pulse_day   -- every shape segment is a pulse trick
//   sweep_day   -- every shape segment is a sweep trick (gaps required)
//   boss_rush   -- bosses appear every 3 chunks instead of every 12
//   speed_demon -- +30% world scroll speed (clamped at the high end)
//   sub_only    -- music drops everything except the sub bass + drums
//   shield_off  -- shield pickups are filtered out at consume time

export type DailyModifierId =
  | "mirror_day"
  | "pulse_day"
  | "sweep_day"
  | "boss_rush"
  | "speed_demon"
  | "sub_only"
  | "shield_off";

export interface DailyModifierDef {
  id: DailyModifierId;
  banner: string;
  description: string;
}

export const DAILY_MODIFIERS: DailyModifierDef[] = [
  {
    id: "mirror_day",
    banner: "MIRROR DAY",
    description: "Every segment swaps your rotate inputs.",
  },
  {
    id: "pulse_day",
    banner: "PULSE DAY",
    description: "Walls black out on every kick. Read fast.",
  },
  {
    id: "sweep_day",
    banner: "SWEEP DAY",
    description: "A kill-bar prowls every segment in time with the snare.",
  },
  {
    id: "boss_rush",
    banner: "BOSS RUSH",
    description: "Bosses on bosses on bosses. Survive.",
  },
  {
    id: "speed_demon",
    banner: "SPEED DEMON",
    description: "World scrolls 30% faster from frame one.",
  },
  {
    id: "sub_only",
    banner: "SUB ONLY",
    description: "All synths muted. Sub bass + drums carry the run.",
  },
  {
    id: "shield_off",
    banner: "NO SHIELDS",
    description: "Shield orbs don't spawn. Eat the wall.",
  },
];

// Deterministic pick. Combines the date-derived seed with a label so
// future modifier additions don't shift older days' picks.
export function pickModifierForDay(dailyKeyStr: string): DailyModifierDef {
  const seed = dailySeed(dailyKeyStr) ^ hashString32("modifier-v1");
  const idx = seed % DAILY_MODIFIERS.length;
  return DAILY_MODIFIERS[idx]!;
}

export function describeModifier(id: DailyModifierId): DailyModifierDef {
  return DAILY_MODIFIERS.find((d) => d.id === id) ?? DAILY_MODIFIERS[0]!;
}
